import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Globe, List, Play, Search, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CopyId, PageHeader, StatusPill } from '@/components/page';
import { API_URL, CodeBlock, EmptyState, Mono, SCOPE_INFO, Tag } from '@/components/oauth';
import { useDebouncedValue } from '@/hooks';
import { cn } from '@/lib/utils';

const J = (o: unknown) => JSON.stringify(o, null, 2);
const CLIENT_ID = 'az_5f0c2d9e1b7a';
const CALLBACK = 'https://app.example.com/callback';
const jwtish = 'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6Ik…';
const ENVELOPE =
  'Management responses use { success, data, pagination? }; errors use { success: false, error: { code, message } }.';

/** [name, in, required, description] — description may contain `code` spans. */
type Param = [string, string, string, string];

const CLIENT_AUTH: Param[] = [
  ['client_id', 'body', 'public', 'Public clients send their ID in the body.'],
  [
    'Authorization',
    'header',
    'confidential',
    '`Basic base64(client_id:client_secret)` for `client_secret_basic`; or send `client_id` + `client_secret` in the body for `client_secret_post`.',
  ],
];
const BEARER: Param[] = [
  [
    'Authorization',
    'header',
    'yes',
    '`Bearer <access_token>` from a console sign-in, or the console session cookie.',
  ],
];

interface Endpoint {
  id: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  summary: string;
  params: Param[];
  req: string;
  res: string;
  resLabel?: string;
}
interface Group {
  id: string;
  title: string;
  desc: string;
  endpoints: Endpoint[];
}

const GROUPS: Group[] = [
  {
    id: 'oidc',
    title: 'OpenID Connect',
    desc: 'Discovery metadata and the public keys your apps use to verify tokens. No authentication required.',
    endpoints: [
      {
        id: 'discovery',
        method: 'GET',
        path: '/.well-known/openid-configuration',
        summary:
          'OpenID Provider metadata — every endpoint, scope and algorithm authrz.me supports. Point your OIDC library here.',
        params: [],
        req: `curl ${API_URL}/.well-known/openid-configuration`,
        res: J({
          issuer: API_URL,
          authorization_endpoint: `${API_URL}/oauth/authorize`,
          token_endpoint: `${API_URL}/oauth/token`,
          userinfo_endpoint: `${API_URL}/oauth/userinfo`,
          jwks_uri: `${API_URL}/.well-known/jwks.json`,
          scopes_supported: ['openid', 'profile', 'email', 'offline_access', 'organizations', 'products'],
          response_types_supported: ['code'],
          grant_types_supported: ['authorization_code', 'refresh_token', 'client_credentials'],
          id_token_signing_alg_values_supported: ['RS256'],
          token_endpoint_auth_methods_supported: ['client_secret_basic', 'client_secret_post', 'none'],
          code_challenge_methods_supported: ['S256'],
          introspection_endpoint: `${API_URL}/oauth/introspect`,
          revocation_endpoint: `${API_URL}/oauth/revoke`,
          end_session_endpoint: `${API_URL}/oauth/logout`,
        }),
      },
      {
        id: 'jwks',
        method: 'GET',
        path: '/.well-known/jwks.json',
        summary:
          'Public RSA keys for verifying ID and access tokens. A rotated key stays published until it expires, so cache by kid.',
        params: [],
        req: `curl ${API_URL}/.well-known/jwks.json`,
        res: J({ keys: [{ kty: 'RSA', use: 'sig', alg: 'RS256', kid: '4f1c…', n: 'xjlCRBqkQ…', e: 'AQAB' }] }),
      },
    ],
  },
  {
    id: 'oauth',
    title: 'OAuth 2.1',
    desc: 'Authorization code with PKCE for users, client credentials for machines, plus token lifecycle endpoints.',
    endpoints: [
      {
        id: 'authorize',
        method: 'GET',
        path: '/oauth/authorize',
        summary:
          'Starts sign-in. authrz.me authenticates the user, asks for consent, then redirects back with a one-time code.',
        params: [
          ['client_id', 'query', 'yes', 'Your client’s ID.'],
          ['redirect_uri', 'query', 'yes', 'Must exactly match a registered URI.'],
          ['response_type', 'query', 'yes', 'Always `code`.'],
          ['scope', 'query', 'no', 'Space-separated. Defaults to `openid`.'],
          ['state', 'query', 'recommended', 'Opaque value echoed back — check it to stop CSRF.'],
          ['code_challenge', 'query', 'public', 'Base64url SHA-256 of your `code_verifier`. Required when the client requires PKCE.'],
          ['code_challenge_method', 'query', 'public', 'Always `S256`.'],
          ['nonce', 'query', 'no', 'Echoed in the ID token to bind it to this request.'],
          ['prompt', 'query', 'no', '`login`, `consent` or `none`.'],
        ],
        req: `GET ${API_URL}/oauth/authorize
  ?client_id=${CLIENT_ID}
  &redirect_uri=${encodeURIComponent(CALLBACK)}
  &response_type=code
  &scope=openid%20profile%20email
  &state=af0ifjsldkj
  &code_challenge=E9Melhoa2OwvFrEMTJguCHaoeK1t8URWbuGJSstw-cM
  &code_challenge_method=S256`,
        resLabel: 'Response · 302',
        res: `HTTP/1.1 302 Found
Location: ${CALLBACK}?code=SplxlOBeZQQYbYS6WxSbIA&state=af0ifjsldkj

# On failure the same redirect carries an error instead:
# ?error=access_denied&error_description=...&state=af0ifjsldkj`,
      },
      {
        id: 'token',
        method: 'POST',
        path: '/oauth/token',
        summary: 'Exchanges a code, refresh token or client credentials for tokens. Form-encoded body.',
        params: [
          ['grant_type', 'body', 'yes', '`authorization_code`, `refresh_token` or `client_credentials`.'],
          ['code', 'body', 'code grant', 'The code from the authorize redirect. Single use.'],
          ['redirect_uri', 'body', 'code grant', 'Same value sent to authorize.'],
          ['code_verifier', 'body', 'PKCE', 'The original verifier behind `code_challenge`.'],
          ['refresh_token', 'body', 'refresh grant', 'A refresh token issued with `offline_access`.'],
          ['scope', 'body', 'no', 'Narrow the scope on refresh or client credentials.'],
          ['resource', 'body', 'no', 'RFC 8707 audience. Must match the app’s resource.'],
          ...CLIENT_AUTH,
        ],
        req: `curl -X POST ${API_URL}/oauth/token \\
  -d grant_type=authorization_code \\
  -d code=SplxlOBeZQQYbYS6WxSbIA \\
  -d redirect_uri=${CALLBACK} \\
  -d client_id=${CLIENT_ID} \\
  -d code_verifier=dBjftJeZ4CVP-mB92K27uhbUJU1p1r_wW1gFWFOEjXk`,
        res: J({
          access_token: jwtish,
          token_type: 'Bearer',
          expires_in: 3600,
          refresh_token: 'azr_8xLOxBtZp8…',
          scope: 'openid profile email offline_access',
          id_token: jwtish,
        }),
      },
      {
        id: 'userinfo',
        method: 'GET',
        path: '/oauth/userinfo',
        summary: 'Returns claims about the signed-in user, limited to the scopes on the access token.',
        params: [['Authorization', 'header', 'yes', '`Bearer <access_token>`']],
        req: `curl ${API_URL}/oauth/userinfo \\
  -H "Authorization: Bearer $ACCESS_TOKEN"`,
        res: J({
          sub: 'u_02',
          name: 'Marcus Bell',
          picture: null,
          email: 'marcus@northwind.dev',
          email_verified: true,
          authz: { product: 'northwind-billing', roles: ['admin'] },
        }),
      },
      {
        id: 'revoke',
        method: 'POST',
        path: '/oauth/revoke',
        summary: 'Revokes an access or refresh token. Always returns 200, even for unknown tokens (RFC 7009).',
        params: [
          ['token', 'body', 'yes', 'The token to revoke.'],
          ['token_type_hint', 'body', 'no', '`access_token` or `refresh_token`.'],
          ...CLIENT_AUTH,
        ],
        req: `curl -X POST ${API_URL}/oauth/revoke \\
  -d token=$REFRESH_TOKEN \\
  -d token_type_hint=refresh_token \\
  -d client_id=${CLIENT_ID}`,
        resLabel: 'Response · 200',
        res: 'HTTP/1.1 200 OK\nContent-Length: 0',
      },
      {
        id: 'introspect',
        method: 'POST',
        path: '/oauth/introspect',
        summary:
          'Tells a resource server whether a token is active and what it grants (RFC 7662). Confidential clients only.',
        params: [
          ['token', 'body', 'yes', 'The token to inspect.'],
          ['token_type_hint', 'body', 'no', '`access_token` or `refresh_token`.'],
          ...CLIENT_AUTH.slice(1),
        ],
        req: `curl -X POST ${API_URL}/oauth/introspect \\
  -u "${CLIENT_ID}:$CLIENT_SECRET" \\
  -d token=$ACCESS_TOKEN`,
        res: J({
          active: true,
          scope: 'openid profile email products',
          client_id: CLIENT_ID,
          sub: 'u_02',
          token_type: 'Bearer',
          exp: 1793000000,
          iat: 1792996400,
          iss: API_URL,
        }),
      },
      {
        id: 'logout',
        method: 'GET',
        path: '/oauth/logout',
        summary:
          'RP-initiated logout. Ends the authrz.me session, notifies front- and back-channel logout URIs, then redirects.',
        params: [
          ['id_token_hint', 'query', 'recommended', 'The ID token you received — identifies the session and client.'],
          ['post_logout_redirect_uri', 'query', 'no', 'Where to send the user afterwards.'],
          ['state', 'query', 'no', 'Echoed back on the redirect.'],
        ],
        req: `GET ${API_URL}/oauth/logout
  ?id_token_hint=$ID_TOKEN
  &post_logout_redirect_uri=https%3A%2F%2Fapp.example.com%2Fsigned-out
  &state=x9kQ2`,
        resLabel: 'Response · 302',
        res: 'HTTP/1.1 302 Found\nLocation: https://app.example.com/signed-out?state=x9kQ2',
      },
    ],
  },
  {
    id: 'mgmt',
    title: 'Management API',
    desc: `Everything this console does, under ${API_URL}/api. Super admins see all tenants; everyone else is scoped to their organizations. ${ENVELOPE}`,
    endpoints: [
      {
        id: 'orgs-list',
        method: 'GET',
        path: '/api/organizations',
        summary: 'Organizations you belong to. Super admins get every organization.',
        params: [...BEARER, ['page', 'query', 'no', 'Default 1.'], ['pageSize', 'query', 'no', 'Default 50, max 100.']],
        req: `curl ${API_URL}/api/organizations \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({
          success: true,
          data: [{ id: 'org_nw', name: 'Northwind Labs', slug: 'northwind', role: 'owner', is_active: true }],
          pagination: { page: 1, pageSize: 50, total: 1, totalPages: 1 },
        }),
      },
      {
        id: 'orgs-create',
        method: 'POST',
        path: '/api/organizations',
        summary: 'Creates an organization. You become its owner.',
        params: [
          ...BEARER,
          ['name', 'body', 'yes', 'Display name.'],
          ['slug', 'body', 'yes', 'URL-safe, unique.'],
          ['website', 'body', 'no', 'https URL.'],
          ['description', 'body', 'no', 'Short summary.'],
        ],
        req: `curl -X POST ${API_URL}/api/organizations \\\n  -H "Authorization: Bearer $TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Quarry Robotics","slug":"quarry"}'`,
        res: J({ success: true, data: { id: 'org_qr', name: 'Quarry Robotics', slug: 'quarry', is_active: true } }),
      },
      {
        id: 'members-add',
        method: 'POST',
        path: '/api/organizations/:id/members',
        summary: 'Adds a user to an organization with a role.',
        params: [
          ...BEARER,
          ['email', 'body', 'yes', 'Existing authrz.me user.'],
          ['role', 'body', 'yes', '`owner`, `admin`, `member` or `viewer`.'],
        ],
        req: `curl -X POST ${API_URL}/api/organizations/org_nw/members \\\n  -H "Authorization: Bearer $TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"email":"ana@northwind.dev","role":"member"}'`,
        res: J({ success: true, data: { user_id: 'u_03', role: 'member' } }),
      },
      {
        id: 'access-grant',
        method: 'POST',
        path: '/api/products/:id/access',
        summary: 'Grants a user access to an app with roles, extra permissions and an optional expiry.',
        params: [
          ...BEARER,
          ['userId', 'body', 'yes', 'User to grant.'],
          ['roles', 'body', 'no', 'Role names defined on the app.'],
          ['permissions', 'body', 'no', 'Extra permission strings.'],
          ['expiresAt', 'body', 'no', 'ISO-8601 timestamp.'],
        ],
        req: `curl -X POST ${API_URL}/api/products/p_billing/access \\\n  -H "Authorization: Bearer $TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"userId":"u_03","roles":["finance"]}'`,
        res: J({
          success: true,
          data: { product_id: 'p_billing', user_id: 'u_03', roles: ['finance'], permissions: [], expires_at: null, is_active: true },
        }),
      },
      {
        id: 'access-update',
        method: 'PATCH',
        path: '/api/products/:id/access/:userId',
        summary: 'Changes roles, permissions, expiry or suspends a grant.',
        params: [
          ...BEARER,
          ['roles', 'body', 'no', 'Replaces the role list.'],
          ['isActive', 'body', 'no', '`false` suspends access.'],
        ],
        req: `curl -X PATCH ${API_URL}/api/products/p_billing/access/u_03 \\\n  -H "Authorization: Bearer $TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"isActive":false}'`,
        res: J({ success: true, data: { user_id: 'u_03', is_active: false } }),
      },
      {
        id: 'access-revoke',
        method: 'DELETE',
        path: '/api/products/:id/access/:userId',
        summary: 'Removes a user’s access to an app.',
        params: [...BEARER],
        req: `curl -X DELETE ${API_URL}/api/products/p_billing/access/u_03 \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({ success: true }),
      },
      {
        id: 'clients-list',
        method: 'GET',
        path: '/api/clients',
        summary: 'OAuth clients you can manage, optionally for one app.',
        params: [
          ...BEARER,
          ['productId', 'query', 'no', 'Limit to one app.'],
          ['search', 'query', 'no', 'Matches name or client ID.'],
          ['page', 'query', 'no', 'Default 1.'],
          ['pageSize', 'query', 'no', 'Default 50, max 100.'],
        ],
        req: `curl "${API_URL}/api/clients?productId=p_billing" \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({
          success: true,
          data: [{ id: 'c_x81k2q', productId: 'p_billing', name: 'Billing dashboard', clientId: CLIENT_ID, clientType: 'confidential', isActive: true }],
          pagination: { page: 1, pageSize: 50, total: 1, totalPages: 1 },
        }),
      },
      {
        id: 'clients-create',
        method: 'POST',
        path: '/api/clients',
        summary: 'Registers a client. Confidential clients get a secret in this response only.',
        params: [
          ...BEARER,
          ['productId', 'body', 'yes', 'App the client belongs to.'],
          ['name', 'body', 'yes', 'Shown on consent.'],
          ['clientType', 'body', 'yes', '`public` or `confidential`.'],
          ['redirectUris', 'body', 'code grant', 'Exact URIs.'],
          ['allowedScopes', 'body', 'no', 'Defaults to `openid profile email`.'],
          ['allowedGrantTypes', 'body', 'no', 'Defaults to code + refresh.'],
        ],
        req: `curl -X POST ${API_URL}/api/clients \\\n  -H "Authorization: Bearer $TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"productId":"p_billing","name":"Billing dashboard","clientType":"confidential","redirectUris":["https://billing.northwind.dev/oauth/callback"]}'`,
        res: J({ success: true, data: { id: 'c_x81k2q', clientId: CLIENT_ID, clientSecret: 'azs_… (shown once)', clientType: 'confidential' } }),
      },
      {
        id: 'clients-rotate',
        method: 'POST',
        path: '/api/clients/:id/rotate-secret',
        summary: 'Issues a new secret for a confidential client. The old one stops working immediately.',
        params: [...BEARER],
        req: `curl -X POST ${API_URL}/api/clients/c_x81k2q/rotate-secret \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({ success: true, data: { clientSecret: 'azs_… (shown once)' } }),
      },
      {
        id: 'clients-integration',
        method: 'GET',
        path: '/api/clients/:id/integration',
        summary: 'Ready-to-paste issuer, endpoints and settings for one client.',
        params: [...BEARER],
        req: `curl ${API_URL}/api/clients/c_x81k2q/integration \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({
          success: true,
          data: {
            issuer: API_URL,
            discovery_url: `${API_URL}/.well-known/openid-configuration`,
            authorization_endpoint: `${API_URL}/oauth/authorize`,
            token_endpoint: `${API_URL}/oauth/token`,
            client_id: CLIENT_ID,
          },
        }),
      },
      {
        id: 'keys-rotate',
        method: 'POST',
        path: '/api/keys/rotate',
        summary: 'Super admin. Generates a new signing key; the previous key keeps verifying until it expires.',
        params: [...BEARER],
        req: `curl -X POST ${API_URL}/api/keys/rotate \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({ success: true, data: { kid: '4f1c0e9a-…', message: 'Signing key rotated.' } }),
      },
      {
        id: 'keys-retire',
        method: 'POST',
        path: '/api/keys/:kid/retire',
        summary: 'Super admin. Removes a non-current key from the JWKS immediately. Tokens it signed stop verifying.',
        params: [...BEARER],
        req: `curl -X POST ${API_URL}/api/keys/4f1c0e9a-…/retire \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({ success: true, data: { kid: '4f1c0e9a-…', retired: true } }),
      },
      {
        id: 'audit-list',
        method: 'GET',
        path: '/api/audit',
        summary: 'Super admin. Audit events, newest first.',
        params: [
          ...BEARER,
          ['action', 'query', 'no', 'One action or a comma-separated list.'],
          ['startDate', 'query', 'no', 'ISO-8601.'],
          ['endDate', 'query', 'no', 'ISO-8601.'],
          ['limit', 'query', 'no', 'Default 50, max 500.'],
          ['offset', 'query', 'no', 'Rows to skip.'],
        ],
        req: `curl "${API_URL}/api/audit?action=auth.login.failed&limit=20" \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({
          success: true,
          data: [{ id: 'evt_8996', action: 'auth.login.failed', resource: 'user', ipAddress: '203.0.113.90', timestamp: '2026-10-22T14:03:11Z' }],
        }),
      },
      {
        id: 'sessions-revoke',
        method: 'POST',
        path: '/api/sessions/revoke-others',
        summary: 'Signs you out everywhere except the current session.',
        params: [...BEARER],
        req: `curl -X POST ${API_URL}/api/sessions/revoke-others \\\n  -H "Authorization: Bearer $TOKEN"`,
        res: J({ success: true, data: { revoked: 3 } }),
      },
      {
        id: 'mcp-create',
        method: 'POST',
        path: '/api/mcp-tokens',
        summary: 'Super admin. Creates a hosted MCP token. The token is returned once.',
        params: [
          ...BEARER,
          ['name', 'body', 'yes', 'What uses the token.'],
          ['scopes', 'body', 'yes', 'From `mcp`, `mcp:read`, `mcp:write`, `mcp:delete`, `mcp:admin`.'],
          ['expiresAt', 'body', 'no', 'ISO-8601; omit for no expiry.'],
        ],
        req: `curl -X POST ${API_URL}/api/mcp-tokens \\\n  -H "Authorization: Bearer $TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Read-only audit bot","scopes":["mcp","mcp:read"]}'`,
        res: J({ success: true, data: { token: { id: 'm4', prefix: 'azmcp_Lp0x' }, rawKey: 'azmcp_… (shown once)' } }),
      },
    ],
  },
];

const GUIDE_PKCE: [string, string][] = [
  [
    '1 · Create a verifier and challenge',
    `// Random verifier, kept in session storage until the callback
function base64url(bytes) {
  return btoa(String.fromCharCode(...bytes)).replace(/\\+/g, '-').replace(/\\//g, '_').replace(/=+$/, '');
}
const codeVerifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
const codeChallenge = base64url(new Uint8Array(digest));`,
  ],
  [
    '2 · Redirect to authorize',
    `const url = new URL('${API_URL}/oauth/authorize');
url.search = new URLSearchParams({
  client_id: 'your_client_id',
  redirect_uri: 'https://yourapp.com/callback',
  response_type: 'code',
  scope: 'openid profile email',
  state: crypto.randomUUID(),
  code_challenge: codeChallenge,
  code_challenge_method: 'S256',
});
location.assign(url);`,
  ],
  [
    '3 · Exchange the code',
    `const res = await fetch('${API_URL}/oauth/token', {
  method: 'POST',
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  body: new URLSearchParams({
    grant_type: 'authorization_code',
    code,                       // from the callback query string
    redirect_uri: 'https://yourapp.com/callback',
    client_id: 'your_client_id',
    code_verifier: codeVerifier,
  }),
});
const tokens = await res.json();
// { access_token, id_token, refresh_token, token_type, expires_in }`,
  ],
];

const VERIFY = `import * as jose from 'jose';

const JWKS = jose.createRemoteJWKSet(new URL('${API_URL}/.well-known/jwks.json'));

export async function verify(token) {
  const { payload } = await jose.jwtVerify(token, JWKS, { issuer: '${API_URL}' });
  return payload; // sub, aud, exp, authz: { product, roles } …
}`;

const SCOPE_ROWS: [string, string][] = [
  ['openid', 'sub, iss, aud, exp, iat, nonce'],
  ['profile', 'name, picture'],
  ['email', 'email, email_verified'],
  ['offline_access', 'refresh_token (not a claim)'],
  ['organizations', 'organization memberships'],
  ['products', 'authz { product, roles }'],
];

const GUIDES = [
  { id: 'guide-pkce', label: 'Quick start', icon: BookOpen, search: 'pkce authorization code flow quick start verifier challenge exchange' },
  { id: 'guide-scopes', label: 'Scopes & claims', icon: List, search: 'scopes claims openid profile email offline_access organizations products authz' },
  { id: 'guide-verify', label: 'Verifying tokens', icon: ShieldCheck, search: 'verify token jwt jose jwks resource server' },
];

/** Render inline `code` spans inside a description string. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split('`').map((part, i) =>
        i % 2 ? (
          <Mono key={i} className="rounded bg-foreground/[0.06] px-1">
            {part}
          </Mono>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

const METHOD_STYLE: Record<string, string> = {
  GET: 'bg-success/[0.12] text-success',
  POST: 'bg-foreground/[0.07] text-foreground',
  PATCH: 'bg-warning/[0.14] text-warning',
  DELETE: 'bg-destructive/[0.12] text-destructive',
};

function Method({ method, small }: { method: string; small?: boolean }) {
  return (
    <span
      className={cn(
        'inline-block shrink-0 rounded font-mono font-bold',
        small ? 'min-w-[38px] px-1 py-px text-center text-[9.5px]' : 'px-1.5 py-0.5 text-[11px]',
        METHOD_STYLE[method]
      )}
    >
      {method}
    </span>
  );
}

const searchText = (e: Endpoint) =>
  [e.method, e.path, e.summary, ...e.params.map((p) => p[0])].join(' ').toLowerCase();

export function DocsPage() {
  const [query, setQuery] = useState('');
  const q = useDebouncedValue(query, 120).trim().toLowerCase();
  const [active, setActive] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);

  const visibleEndpoints = useMemo(
    () => new Set(GROUPS.flatMap((g) => g.endpoints).filter((e) => !q || searchText(e).includes(q)).map((e) => e.id)),
    [q]
  );
  const visibleGuides = useMemo(
    () => new Set(GUIDES.filter((g) => !q || g.search.includes(q)).map((g) => g.id)),
    [q]
  );
  const shown = visibleEndpoints.size + visibleGuides.size;

  // Highlight the section in view in the index.
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActive(en.target.id);
        });
      },
      { rootMargin: '-80px 0px -70% 0px' }
    );
    bodyRef.current?.querySelectorAll('[data-spy]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [q]);

  // Scroll to a hash target on first load (e.g. /docs#ep-authorize).
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  const navLink = (id: string, children: React.ReactNode) => (
    <a
      key={id}
      href={`#${id}`}
      aria-current={active === id ? 'true' : undefined}
      className={cn(
        'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
        active === id && 'bg-foreground/[0.06] font-medium text-foreground'
      )}
    >
      {children}
    </a>
  );

  return (
    <div>
      <PageHeader
        title="API reference"
        description="OAuth 2.1 and OpenID Connect endpoints for your apps, plus the management API behind this console."
        actions={
          <Button variant="outline" asChild>
            <Link to="/playground">
              <Play className="mr-2 h-4 w-4" />
              Open playground
            </Link>
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:items-start">
        <nav
          aria-label="API reference contents"
          className="flex flex-col gap-3.5 lg:sticky lg:top-20 lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto lg:pb-3"
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              aria-label="Filter endpoints"
              placeholder="Filter endpoints"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              autoComplete="off"
            />
          </div>
          <div className="hidden space-y-3.5 lg:block">
            {GUIDES.some((g) => visibleGuides.has(g.id)) && (
              <div>
                <p className="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">Guides</p>
                {GUIDES.filter((g) => visibleGuides.has(g.id)).map((g) =>
                  navLink(g.id, (
                    <>
                      <g.icon className="h-3.5 w-3.5 shrink-0" />
                      <span>{g.label}</span>
                    </>
                  ))
                )}
              </div>
            )}
            {GROUPS.map((g) => {
              const eps = g.endpoints.filter((e) => visibleEndpoints.has(e.id));
              if (!eps.length) return null;
              return (
                <div key={g.id}>
                  <p className="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">{g.title}</p>
                  {eps.map((e) =>
                    navLink(`ep-${e.id}`, (
                      <>
                        <Method method={e.method} small />
                        <span className="min-w-0 truncate font-mono text-[12px]" title={e.path}>
                          {e.path.replace(/^\/api/, '')}
                        </span>
                      </>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div ref={bodyRef} className="flex min-w-0 flex-col gap-9">
          {!q && (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
                <Globe className="mt-0.5 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div>
                    <b className="font-semibold">Base URL</b>
                    <p className="text-[13px] text-muted-foreground">Issuer and every endpoint below.</p>
                  </div>
                  <CopyId value={API_URL} />
                </div>
              </div>
              <Link
                to="/playground"
                className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 hover:border-foreground/30"
              >
                <Play className="mt-0.5 h-[18px] w-[18px] shrink-0 text-muted-foreground" />
                <div>
                  <b className="font-semibold">OAuth playground</b>
                  <p className="text-[13px] text-muted-foreground">
                    Walk a real authorize → callback → token flow with one of your clients.
                  </p>
                </div>
              </Link>
            </div>
          )}

          {visibleGuides.has('guide-pkce') && (
            <section id="guide-pkce" data-spy className="scroll-mt-20 space-y-3.5">
              <SectionTitle title="Quick start · authorization code + PKCE">
                The recommended flow for every app that signs users in. Public clients must use it;
                confidential clients should.
              </SectionTitle>
              <div className="space-y-3.5 rounded-lg border border-border bg-card p-4">
                {GUIDE_PKCE.map(([label, code]) => (
                  <CodeBlock key={label} label={label} code={code} />
                ))}
              </div>
            </section>
          )}

          {visibleGuides.has('guide-scopes') && (
            <section id="guide-scopes" data-spy className="scroll-mt-20 space-y-3.5">
              <SectionTitle title="Scopes & claims">
                Request only what you need — users see each scope on the consent screen.
              </SectionTitle>
              <div className="overflow-hidden rounded-lg border border-border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Scope</TableHead>
                      <TableHead>Grants</TableHead>
                      <TableHead>Claims</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {SCOPE_ROWS.map(([s, claims]) => (
                      <TableRow key={s}>
                        <TableCell className="whitespace-nowrap">
                          <Mono>{s}</Mono>{' '}
                          {s === 'openid' && <StatusPill>required for OIDC</StatusPill>}
                        </TableCell>
                        <TableCell>{SCOPE_INFO[s]}</TableCell>
                        <TableCell>
                          <Mono className="text-muted-foreground">{claims}</Mono>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
          )}

          {visibleGuides.has('guide-verify') && (
            <section id="guide-verify" data-spy className="scroll-mt-20 space-y-3.5">
              <SectionTitle title="Verifying tokens">
                Tokens are RS256 JWTs. Verify the signature against the JWKS and check{' '}
                <Mono>iss</Mono>, <Mono>aud</Mono> and <Mono>exp</Mono> before trusting any claim.
              </SectionTitle>
              <div className="rounded-lg border border-border bg-card p-4">
                <CodeBlock label="Node.js · jose" code={VERIFY} />
              </div>
            </section>
          )}

          {GROUPS.map((g) => {
            const eps = g.endpoints.filter((e) => visibleEndpoints.has(e.id));
            if (!eps.length) return null;
            return (
              <section key={g.id} id={`grp-${g.id}`} className="scroll-mt-20 space-y-3.5">
                <SectionTitle title={g.title}>{g.desc}</SectionTitle>
                <div className="flex flex-col gap-4">
                  {eps.map((e) => (
                    <article
                      key={e.id}
                      id={`ep-${e.id}`}
                      data-spy
                      aria-labelledby={`ep-${e.id}-t`}
                      className="scroll-mt-20 min-w-0 overflow-hidden rounded-lg border border-border bg-card"
                    >
                      <div className="space-y-2 border-b border-border px-4 py-3.5">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <Method method={e.method} />
                          <h3 className="sr-only" id={`ep-${e.id}-t`}>
                            {e.method} {e.path}
                          </h3>
                          <CopyId value={API_URL + e.path} className="max-w-full" />
                        </div>
                        <p className="text-sm text-muted-foreground">{e.summary}</p>
                      </div>
                      <div className="space-y-3.5 p-4">
                        {e.params.length > 0 && (
                          <div className="overflow-hidden rounded-lg border border-border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Parameter</TableHead>
                                  <TableHead>In</TableHead>
                                  <TableHead>Required</TableHead>
                                  <TableHead>Description</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {e.params.map(([n, where, req, d]) => (
                                  <TableRow key={n}>
                                    <TableCell className="whitespace-nowrap">
                                      <Mono>{n}</Mono>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">{where}</TableCell>
                                    <TableCell>
                                      {req === 'yes' ? (
                                        <Tag solid>yes</Tag>
                                      ) : (
                                        <span className="text-muted-foreground">{req}</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="min-w-[260px] whitespace-normal">
                                      <Rich text={d} />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                        <div className="grid gap-3 min-[1180px]:grid-cols-2">
                          <CodeBlock label="Request" code={e.req} />
                          <CodeBlock label={e.resLabel || 'Response · 200'} code={e.res} />
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}

          {shown === 0 && (
            <div className="rounded-lg border border-border bg-card">
              <EmptyState
                icon={<Search />}
                title="No endpoints match"
                action={
                  <Button variant="outline" onClick={() => setQuery('')}>
                    Clear filter
                  </Button>
                }
              >
                Try a path like <Mono>/oauth/token</Mono>, a method, or a parameter name.
              </EmptyState>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <h2 className="text-lg font-semibold tracking-[-0.02em]">{title}</h2>
      <p className="max-w-[70ch] text-muted-foreground">{children}</p>
    </div>
  );
}
